import React, { memo, useCallback } from 'react';
import { useAppSelector } from 'store/hooks';
import { CardView, SettingsItemDescription, SettingsItemNavigate, SettingsItemTitle, Spacer } from 'design-system';
import { IconArrowBack, IconBoldCalendar, IconMoneyFromBracket, IconSettingsBold } from 'design-system/icons';
import { IconCalendar2 } from 'design-system/icons/IconCalendar2';
import { IconMoneyChange } from 'design-system/icons/IconMoneyChange';
import IconPieChart from 'design-system/icons/IconPieChart';
import SectionHeader from 'design-system/SectionHeader';
import Status from 'design-system/Status';
import useColors from 'hooks/useColors';
import { formatUpcomingDate } from 'features/invest/utils/autoinvest';
import { selectAutosaveStatusObj } from 'reducers/selectors';
import Modal from 'utils/packages/Modal';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { showUnavailableOnWeb } from 'utils/webGate';
import { selectAutosaveCollectionFrequency, selectLatestAutosaveActivity, selectShouldShowAutosaveSelectPeriod, } from '../reducers/selectors';
import { formatNextCollectionDate } from '../utils';
const collectionFrequencyDisplayMap = {
    WEEKLY: 'Weekly',
    MONTHLY: 'Monthly',
};
const AutosaveSettingsSection = () => {
    const colors = useColors();
    const navigation = useMainStackNavigation();
    const autosaveStatusObj = useAppSelector(selectAutosaveStatusObj);
    const collectionFrequency = useAppSelector(selectAutosaveCollectionFrequency);
    const isAutomated = useAppSelector((state) => state.pots.savingsPlan?.automated);
    const customAmount = useAppSelector((state) => state.pots.savingsPlan?.customAmount);
    const allowOverdraft = useAppSelector((state) => state.pots.savingsPlan?.allowOverdraft);
    const allocationText = useAppSelector((state) => state.pots.savingsPlan?.allocationText);
    const shouldShowAutosaveSelectPeriod = useAppSelector(selectShouldShowAutosaveSelectPeriod);
    const roundUpMultiplier = useAppSelector((state) => state.pots.savingsPlan?.roundUpMultiplier);
    const nextPaymentCollectionDate = useAppSelector((state) => state.pots.savingsPlan?.nextPaymentCollectionDate);
    const latestActivity = useAppSelector(selectLatestAutosaveActivity);
    const isActivityPending = latestActivity?.status === 'PENDING';
    const isBlockedOnWeb = () => showUnavailableOnWeb('emma://auto-save', 'Manage Autosave');
    const navigateWithPendingCheck = useCallback((navigate) => {
        if (isActivityPending && latestActivity) {
            const activityDate = formatUpcomingDate(latestActivity.timestamp);
            Modal.showConfirmation({
                title: 'Autosave is in progress',
                subtitle: [
                    `We\u2019re currently processing your scheduled payment for ${activityDate}.`,
                    'Any changes you make will apply to future collections.',
                ],
                confirmButtonTitle: 'Okay',
                onConfirm: navigate,
            });
        }
        else {
            navigate();
        }
    }, [isActivityPending, latestActivity]);
    const handleOverdrafts = useCallback(() => {
        if (isBlockedOnWeb()) {
            return;
        }
        navigation.navigateDeprecated('AutosaveOverdraftSettings');
    }, []);
    const handleAllocation = useCallback(() => {
        if (isBlockedOnWeb()) {
            return;
        }
        navigation.navigateDeprecated('AutosaveDistribution', { isEditFlow: true });
    }, []);
    const handleAISavings = useCallback(() => {
        if (isBlockedOnWeb()) {
            return;
        }
        navigateWithPendingCheck(() => {
            navigation.navigateDeprecated('AutosaveAISavingsSettings');
        });
    }, [navigateWithPendingCheck]);
    const handleCustomDeposits = useCallback(() => {
        if (isBlockedOnWeb()) {
            return;
        }
        navigateWithPendingCheck(() => {
            navigation.navigateDeprecated('AutosaveCustomDepositSettings');
        });
    }, [navigateWithPendingCheck]);
    const handleRoundUps = useCallback(() => {
        if (isBlockedOnWeb()) {
            return;
        }
        navigateWithPendingCheck(() => {
            navigation.navigateDeprecated('AutosaveRoundupSettings');
        });
    }, [navigateWithPendingCheck]);
    const handleCollectionFrequency = useCallback(() => {
        if (isBlockedOnWeb()) {
            return;
        }
        navigation.navigateDeprecated('AutosaveChoosePeriod');
    }, []);
    const chooseNextCollection = useCallback(() => {
        if (isBlockedOnWeb()) {
            return;
        }
        navigation.navigateDeprecated('AutosaveChooseMonthlyFrequency', { isEditFrequency: true });
    }, []);
    return (<>
      <Spacer h={24}/>

      <SectionHeader horizontalMargins>Bank balance protection</SectionHeader>
      <Spacer.H16 />
      <CardView noMarginTop noMarginBottom webPadding>
        <SettingsItemNavigate icon={<IconMoneyFromBracket gradientKey="apricot"/>} onPress={handleOverdrafts}>
          <SettingsItemDescription>Overdraft deposits</SettingsItemDescription>
          <Spacer h={4}/>
          <SettingsItemTitle>
            <Status inActiveColor={colors.status.offline} active={!!allowOverdraft} activeText="On" inactiveText="Off"/>
          </SettingsItemTitle>
        </SettingsItemNavigate>
      </CardView>

      <Spacer h={24}/>

      <SectionHeader horizontalMargins>Saving rules</SectionHeader>
      <Spacer.H16 />
      <CardView noMarginTop noMarginBottom webPadding>
        <SettingsItemNavigate icon={<IconSettingsBold gradientKey="apricot"/>} onPress={handleAISavings}>
          <SettingsItemDescription>AI savings</SettingsItemDescription>
          <Spacer h={4}/>
          <SettingsItemTitle>
            <Status activeText="On" inactiveText="Off" inActiveColor={colors.status.offline} active={!autosaveStatusObj?.isPaused && !!isAutomated}/>
          </SettingsItemTitle>
        </SettingsItemNavigate>
        <SettingsItemNavigate icon={<IconCalendar2 />} onPress={handleCustomDeposits}>
          <SettingsItemDescription>Custom deposit</SettingsItemDescription>
          <Spacer h={4}/>
          <SettingsItemTitle>
            <Status activeText="On" inactiveText="Off" inActiveColor={colors.status.offline} active={!autosaveStatusObj?.isPaused && !!customAmount}/>
          </SettingsItemTitle>
        </SettingsItemNavigate>
        <SettingsItemNavigate icon={<IconMoneyChange />} onPress={handleRoundUps}>
          <SettingsItemDescription>Round Ups</SettingsItemDescription>
          <Spacer h={4}/>
          <SettingsItemTitle>
            <Status activeText="On" inactiveText="Off" inActiveColor={colors.status.offline} active={!autosaveStatusObj?.isPaused && !!roundUpMultiplier}/>
          </SettingsItemTitle>
        </SettingsItemNavigate>
      </CardView>

      <Spacer h={24}/>

      <SectionHeader horizontalMargins>Collection details</SectionHeader>
      <Spacer.H16 />
      <CardView noMarginTop noMarginBottom webPadding>
        <SettingsItemNavigate title={allocationText} description="Allocation" onPress={handleAllocation} icon={<IconPieChart gradientKey="yellow"/>}/>
        {!!collectionFrequency && (<SettingsItemNavigate description="Collection frequency" onPress={handleCollectionFrequency} icon={<IconArrowBack gradientKey="apricot"/>} disabled={!shouldShowAutosaveSelectPeriod || autosaveStatusObj?.isPaused} title={collectionFrequencyDisplayMap[collectionFrequency]}/>)}
        {autosaveStatusObj &&
            !autosaveStatusObj.isPaused &&
            !autosaveStatusObj.actionRequired &&
            !!nextPaymentCollectionDate ? (<SettingsItemNavigate description="Next collection" onPress={chooseNextCollection} disabled={collectionFrequency === 'WEEKLY'} icon={<IconBoldCalendar gradientKey="skyBlue"/>} title={formatNextCollectionDate(nextPaymentCollectionDate)}/>) : null}
      </CardView>
    </>);
};
export default memo(AutosaveSettingsSection);
